import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountProvider,
  DiscountValueType,
  ExpirationType,
} from "../discount";

const expireAfterDaysSchema = z.object({
  expirationType: z.literal(ExpirationType.EXPIRATION_DAYS),
  days: z.number(),
});
const expireAfterDateSchema = z.object({
  expirationType: z.literal(ExpirationType.DATE),
  date: z.date(),
});
const expireNeverSchema = z.object({
  expirationType: z.literal(ExpirationType.NEVER),
});
const expireAfterDeliverySchema = z.object({
  expirationType: z.literal(ExpirationType.DELIVERY),
});

export const expirationSchema = z.discriminatedUnion("expirationType", [
  expireAfterDaysSchema,
  expireAfterDateSchema,
  expireNeverSchema,
  expireAfterDeliverySchema,
]);

// Define schemas for discount settings types
const combinesWithSchema = z.object({
  orderDiscounts: z.boolean().optional(),
  productDiscounts: z.boolean().optional(),
  shippingDiscount: z.boolean().optional(),
});

export enum DiscountSettingsType {
  SHOPIFY_BASIC = "SHOPIFY_BASIC",
  SHOPIFY_FREE_SHIPPING = "SHOPIFY_FREE_SHIPPING",
  SHOPIFY_BXGY = "SHOPIFY_BXGY",
}

export enum BxgyTriggerType {
  MINIMUM_QUANTITY = "minimumQuantity",
  MINIMUM_AMOUNT = "minimumAmount",
}

export enum DiscountCategory {
  RECOVER = "RECOVER",
  SHOPPER_AI = "SHOPPER_AI",
  MARKETING_AGENT = "MARKETING_AGENT",
}

export type BasicShopifyDiscountConfiguration = z.infer<
  typeof basicShopifyDiscountSchema
>;
const basicShopifyDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_BASIC),
  discountValueType: createEnum(DiscountValueType),
  discountValueAmount: z.number(),
  combinesWith: combinesWithSchema,
  excludeItemsOnSale: z.boolean().optional(),
  appliesToCollections: z.array(z.string()).optional(),
  appliesToProducts: z.array(z.string()).optional(),
  endsAt: z.date().optional(),
  minimumRequirement: z
    .object({
      type: createEnum(DiscountMinRequirementType),
      minimum: z.number(),
    })
    .optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  startsAt: z.date().optional(),
});

// Free Shipping discount schema
export type FreeShippingDiscountConfiguration = z.infer<
  typeof freeShippingDiscountSchema
>;
const freeShippingDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_FREE_SHIPPING),
  combinesWith: combinesWithSchema,
  maximumShippingPrice: z.number().optional(),
  countryCodes: z.array(z.string()).optional(),
  minimumRequirement: z
    .object({
      type: createEnum(DiscountMinRequirementType),
      minimum: z.number(),
    })
    .optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  startsAt: z.date().optional(),
  endsAt: z.date().optional(),
});

// Buy X Get Y discount schema
const bxgyProductSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
});

const bxgyCollectionSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
});

export type BxgyDiscountConfiguration = z.infer<typeof bxgyDiscountSchema>;
const bxgyDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_BXGY),
  combinesWith: combinesWithSchema,
  customerBuys: z.object({
    triggerType: createEnum(BxgyTriggerType),
    quantity: z.number().optional(),
    amount: z.number().optional(),
    products: z.array(bxgyProductSchema).optional(),
    collections: z.array(bxgyCollectionSchema).optional(),
  }),
  customerGets: z.object({
    products: z.array(bxgyProductSchema).optional(),
    collections: z.array(bxgyCollectionSchema).optional(),
    quantity: z.number(),
    discountValueType: createEnum(DiscountValueType),
    discountValue: z.number(),
  }),
  usesPerOrderLimit: z.number().positive().optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  startsAt: z.date().optional(),
  endsAt: z.date().optional(),
});

// Using discriminatedUnion for extensibility
export const discountSettingsSchema = z.discriminatedUnion("settingsType", [
  basicShopifyDiscountSchema,
  freeShippingDiscountSchema,
  bxgyDiscountSchema,
]);
export type DiscountSettings = z.infer<typeof discountSettingsSchema>;

export const databaseDiscountSchema = z.object({
  title: z.string(),
  provider: createEnum(DiscountProvider),
  groupType: createEnum(DiscountGroupType),
  discountId: z.string(),
  code: z.string(),
  expiration: expirationSchema,
  discountSettings: discountSettingsSchema.optional(),
  category: createEnum(DiscountCategory).optional(),
});

export type RedoDatabaseDiscount = z.infer<typeof DiscountDbParser>;
export const DiscountDbParser = z.intersection(
  databaseDiscountSchema,
  z.object({ _id: zExt.objectId(), teamId: zExt.objectId() }),
);
